import * as Icons from "@mui/icons-material";
import { Box, Fade, IconButton, InputLabel, useTheme } from "@mui/material";
import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { type FormEvent, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { SearchFeedsResponse, SearchMessageResponse } from "@storyboard/web-client";
import { isSearchUnavailable, searchChannels, searchKeys, searchMessages } from "@storyboard/web-client";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import SearchResults from "@/components/Search/SearchResults";
import { nextSearchOffset, searchPagesToPaginatedData } from "@/components/Search/search";
import { FullInput } from "@/components/Utils";
import { ampli, transport } from "@/config";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import { DataContext } from "@/models/DataProvider";
import { labelChannelSearchPage, labelMessageSearchPage } from "@/models/demoLabels";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { UxContext } from "@/models/UxStateProvider";
import { retryUnlessClientError, staleTimes } from "@/queryClient";
import "./styles.css";

/** Message search answers with authors and channel titles labeled in demo mode (see `labelMessageSearchPage`). */
const selectMessagePages = (data: InfiniteData<SearchMessageResponse, number>) => ({
  ...data,
  pages: data.pages.map(labelMessageSearchPage),
});

/** Channel search answers with alias channel titles labeled in demo mode (see `labelChannelSearchPage`). */
const selectChannelPages = (data: InfiniteData<SearchFeedsResponse, number>) => ({
  ...data,
  pages: data.pages.map(labelChannelSearchPage),
});

export default function SearchContainer() {
  const theme = useTheme();
  const { preferredLanguage } = useContext(DataContext);
  const { globalSearchOpen, setGlobalSearchOpen } = useContext(UxContext);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [queryString, setQueryString] = useState<string>("");
  const [submittedQuery, setSubmittedQuery] = useState<string>("");
  const [disableEnterKey, setDisableEnterKey] = useState<boolean>(false);
  const [resultsIndex, setResultsIndex] = useState<number | null>(null);
  const globalSearchInputRef = useRef<HTMLInputElement | null>(null);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);

  const globalSearch = true;

  const limit = 10;
  const channelLimit = 4;

  // A search the environment has no index for fails the same way every time, so it is never retried.
  const retrySearch = (failureCount: number, error: unknown) =>
    !isSearchUnavailable(error) && retryUnlessClientError(failureCount, error);

  // The two searches are independent queries; a failure of one must not discard the other's
  // results, and a failed load-more keeps the already-fetched pages for a retry.
  const messagesQuery = useInfiniteQuery({
    queryKey: searchKeys.messages(workspaceId, { query: submittedQuery, languages: [preferredLanguage], limit }),
    queryFn: ({ pageParam }) =>
      searchMessages(transport, {
        workspaceId,
        query: submittedQuery,
        languages: [preferredLanguage],
        limit,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages) => nextSearchOffset({ pages: allPages, limit }),
    enabled: Boolean(submittedQuery) && Boolean(workspaceId),
    staleTime: staleTimes.search,
    retry: retrySearch,
    select: selectMessagePages,
  });
  const channelsQuery = useInfiniteQuery({
    queryKey: searchKeys.channels(workspaceId, { query: submittedQuery, limit: channelLimit }),
    queryFn: ({ pageParam }) =>
      searchChannels(transport, { workspaceId, query: submittedQuery, limit: channelLimit, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages) => nextSearchOffset({ pages: allPages, limit: channelLimit }),
    enabled: Boolean(submittedQuery) && Boolean(workspaceId),
    staleTime: staleTimes.search,
    retry: retrySearch,
    select: selectChannelPages,
  });

  const messages = useMemo(
    () => searchPagesToPaginatedData({ pages: messagesQuery.data?.pages, query: submittedQuery, limit }),
    [messagesQuery.data, submittedQuery],
  );
  const channels = useMemo(
    () => searchPagesToPaginatedData({ pages: channelsQuery.data?.pages, query: submittedQuery, limit: channelLimit }),
    [channelsQuery.data, submittedQuery],
  );

  const loading =
    (messagesQuery.isFetching && !messagesQuery.isFetchingNextPage) ||
    (channelsQuery.isFetching && !channelsQuery.isFetchingNextPage);
  // An unavailable search is an errored one, so it counts toward noResults the same way.
  const messagesUnavailable = isSearchUnavailable(messagesQuery.error);
  const channelsUnavailable = isSearchUnavailable(channelsQuery.error);
  const noResults =
    hasSearched &&
    !loading &&
    (messagesQuery.isError || messages?.results?.length === 0) &&
    (channelsQuery.isError || channels?.results?.length === 0);

  // Keyboard navigation stays parked until shortly after fresh results render.
  const wasLoading = useRef(false);
  useEffect(() => {
    const settled = wasLoading.current && !loading;
    wasLoading.current = loading;
    if (settled) {
      const timeout = setTimeout(() => setResultsIndex(() => null), 250);
      return () => clearTimeout(timeout);
    }
  }, [loading]);

  const closeGlobalSearch = () => {
    setGlobalSearchOpen(() => false);
    setSubmittedQuery(() => "");
  };

  const keyDownHandler = (event: KeyboardEvent) => {
    event.stopPropagation();
    if ((event.ctrlKey || event.metaKey) && event.key === "k") {
      event.preventDefault();
      setGlobalSearchOpen(() => true);
    } else if (event.key === "Escape" && globalSearchOpen) {
      closeGlobalSearch();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", keyDownHandler);
    return () => {
      window.removeEventListener("keydown", keyDownHandler);
    };
  }, [keyDownHandler]);

  const setInputFocus = (value: boolean) => {
    const input = document.getElementById("globalSearchInput");
    if (value === true) {
      input?.focus();
    } else {
      input?.blur();
    }
    setDisableEnterKey(() => value);
  };

  const submit = (e: FormEvent | null) => {
    if (e) {
      e.preventDefault();
    }
    if (queryString?.length === 0) {
      return;
    }
    ampli.searchExecute({ workspaceId });
    setHasSearched(() => true);
    setResultsIndex(() => -1);
    if (queryString === submittedQuery) {
      void messagesQuery.refetch();
      void channelsQuery.refetch();
    } else {
      setSubmittedQuery(() => queryString);
    }
  };

  const loadMore = () => {
    if (!messagesQuery.isFetchingNextPage) {
      void messagesQuery.fetchNextPage();
    }
  };

  const loadMoreChannels = () => {
    if (!channelsQuery.isFetchingNextPage) {
      void channelsQuery.fetchNextPage();
    }
  };

  const resetValue = () => {
    setHasSearched(() => false);
    setQueryString(() => "");
    setSubmittedQuery(() => "");
    const input = globalSearchInputRef?.current?.children[0]?.querySelectorAll("input")[0];
    if (input) {
      input.focus();
    }
  };

  useEffect(() => {
    if (!globalSearchOpen && queryString) {
      resetValue();
    }
  }, [globalSearchOpen, queryString, resetValue]);

  if (!globalSearch) return null;
  return (
    <>
      {globalSearchOpen && (
        <Box className="global-search-container" sx={{ p: 2 }}>
          <Fade in={globalSearchOpen} timeout={500}>
            <Box
              className="global-search-shell"
              sx={{
                width: "100%",
                maxWidth: "680px",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <Box
                sx={{
                  background: theme.palette.secondary.dark,
                  borderRadius: 4,
                }}
              >
                <Box component="form" onSubmit={(e) => submit(e)}>
                  <Box
                    sx={{
                      padding: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <InputLabel
                      htmlFor={"globalSearchInput"}
                      sx={{
                        flex: "0 1 auto",
                        display: "flex",
                        alignItems: "center",
                        pl: 1,
                        mr: 1,
                      }}
                    >
                      <Icons.Search role="img" />
                    </InputLabel>
                    <Box sx={{ flexGrow: 1 }} ref={globalSearchInputRef}>
                      <FullInput
                        id="globalSearchInput"
                        data-sb={TestIds.search.input}
                        onChange={(e) => setQueryString(() => e?.target?.value)}
                        onClick={() => {
                          setInputFocus(true);
                        }}
                        placeholder="Search across all channels"
                        autoFocus
                        value={queryString}
                        sx={{ width: "100%" }}
                        inputProps={{
                          autoComplete: "off",
                          sx: {
                            fontSize: "1rem !important",
                            minHeight: "0px !important",
                            height: "auto !important",
                            padding: "0.5rem !important",
                            background: "transparent !important",
                            width: "100%",
                            border: "none !important",
                            outline: "none",
                          },
                        }}
                      />
                    </Box>
                    {queryString?.length > 0 && (
                      <IconButton
                        sx={{
                          background: "transparent",
                          p: "0.3rem !important",
                          "&:hover": {
                            background: "transparent !important",
                          },
                          "&:active": {
                            border: "solid 1px transparent !important",
                          },
                        }}
                        data-sb={TestIds.search.clear}
                        aria-label="Cancel Search"
                        onClick={() => resetValue()}
                      >
                        <Icons.CancelRounded />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <SearchResults
                  resultsIndex={resultsIndex}
                  disableEnterKey={disableEnterKey}
                  loading={loading}
                  messages={messages}
                  channels={channels}
                  messagesUnavailable={messagesUnavailable}
                  channelsUnavailable={channelsUnavailable}
                  hasSearched={hasSearched}
                  noResults={noResults}
                  query={queryString}
                  staticQueryString={submittedQuery}
                  setBlur={(value) => setInputFocus(value)}
                  loadPaginatedData={() => loadMore()}
                  disabledLoadMoreButton={messagesQuery.isFetchingNextPage}
                  loadMoreChannels={() => loadMoreChannels()}
                  initializeSearch={() => submit(null)}
                  disableShowMore={channelsQuery.isFetchingNextPage}
                />
              </Box>
            </Box>
          </Fade>

          <Fade in={globalSearchOpen} timeout={250}>
            <Box className="global-search-overlay" onClick={closeGlobalSearch} />
          </Fade>
        </Box>
      )}
    </>
  );
}
